// 1. Import the extendTheme function
import { extendTheme } from '@chakra-ui/react';

const config = {
  useSystemColorMode: false,
  initialColorMode: 'light',
};

// 2. Extend the theme to include custom colors, fonts, etc
const colors = {
  colors: {
    navy: '#004C97',
    teal: '#00B0B9',
    orange: '#E35205',
    lightBlue: '#DDEBF2',
    darkBlue: '#091048',
    grey: {
      lightest: '#f8f7f6',
      light: '#F3F0ED',
      medium: '#d6cdc4',
      blue: '#d1d8e5',
    },
    zinc: {
      50: '#FAFAFA',
      100: '#F4F4F5',
      200: '#E4E4E7',
      300: '#D4D4D8',
      400: '#A1A1AA',
      500: '#71717A',
      600: '#52525B',
      700: '#3F3F46',
      800: '#27272A',
      900: '#18181B',
      950: '#09090B',
    },
    'what-we-do': '#FAC4AF',
    'who-we-are': '#CBEBFA',
    'in-focus': '#FCE3B4',
    'message-from-the-md': '#F3F9D0',
    agreement: {
      sba: '#328',
      eff: '#173',
      fcl: '#4a9',
      ecf: '#8ce',
      scf: '#dc7',
      rcf: '#c67',
      rfi: '#a49',
      rst: '#a7786e',
      sll: '#a2a2a2',
      pll: '#f096c9',
      rsf: '#7E2954',
    },
  },
};

const sizes = {
  sizes: {
    container: {
      sm: '640px',
      md: '800px',
      lg: '1024px',
      xl: '1500px',
      xxl: '1920px',
    },
  },
};

const fontSizes = {
  fontSizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
    '6xl': '3.75rem',
    '7xl': '4.5rem',
    '8xl': '6rem',
    '9xl': '8rem',
  },
};

const spacing = {
  space: {
    1: '30px', // Defaults
    5: '0.3125rem',
    10: '0.625rem',
    20: '1.25rem',
    30: '1.875rem',
    40: '2.5rem',
    50: '3.125rem',
    60: '3.75rem',
    70: '4.375rem',
    80: '5rem',
    90: '5.625rem',
    100: '6.25rem',
  },
};

const textStyles = {
  textStyles: {
    h1: {
      fontSize: ['2.25rem', '3rem', '3.75rem'],
      lineHeight: '1',
      fontWeight: '600',
    },
    h2: {
      fontSize: ['1.5rem', '1.875rem', '2.25rem'],
      lineHeight: '1',
      fontWeight: 'bold',
    },
    h3: {
      fontSize: ['1.25rem', '1.375rem', '1.375rem'],
      lineHeight: '1.2',
      fontWeight: 'bold',
    },
    h4: {
      fontSize: ['1rem', '1.125rem', '1.25rem'],
      lineHeight: '1.2',
      fontWeight: 'bold',
    },
    h5: {
      fontSize: ['0.875rem', '1rem', '1.125rem'],
      lineHeight: '1.2',
      fontWeight: 'bold',
    },
    h6: {
      fontSize: ['0.75rem', '0.875rem', '1rem'],
      lineHeight: '1.2',
      fontWeight: 'bold',
    },
    body: {
      fontSize: ['1rem', '1rem', '1.125rem'],
      lineHeight: '1.6',
      fontWeight: '300',
      fontStyle: 'normal',
    },
    navItem: {
      fontSize: '0.875rem',
      lineHeight: '1',
      textTransform: 'uppercase',
      fontWeight: '700',
      letterSpacing: '1px',
    },
    secondary: {
      fontFamily: 'Georgia, Times, Times New Roman, serif',
    },
  },
};

const components = {
  components: {
    Button: {
      variants: {
        primary: {
          fontWeight: '600',
          textTransform: 'uppercase',
          bg: 'navy',
          color: 'white',
          transition: 'all 0.25s',
          padding: '0.625rem',
          borderRadius: '3px',
          fontSize: '0.882rem',
          _hover: {
            bg: 'orange',
          },
        },
        secondary: {
          fontWeight: 'bold',
          bg: 'transparent',
          color: 'navy',
          transition: 'all 0.25s',
          border: '2px solid navy',
          padding: '0.9375rem 0.625rem',
          borderRadius: '3.125rem',
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          letterSpacing: '0.5px',
          _hover: {
            bg: 'navy',
            color: 'white',
            border: '2px solid transparent',
          },
        },
        link: {
          bg: 'none',
          color: 'navy',
          padding: '0',
          minWidth: 'auto',
          paddingInline: '0',
          _hover: {
            bg: 'none',
            border: 'none',
            textDecoration: 'underline',
          },
        },
      },
    },
    Link: {
      variants: {
        buttonPrimary: {
          fontWeight: 'bold',
          transition: 'all 0.25s',
          display: 'inline-block',
          color: 'black',
          bg: 'orange',
          borderRadius: '5px',
          padding: '10px',
          textTransform: 'uppercase',
          fontSize: '0.875rem',
          _hover: {
            bg: '#FF8200',
            textDecoration: 'none',
          },
        },
        buttonSecondary: {
          fontWeight: 'bold',
          transition: 'all 0.25s',
          display: 'inline-block',
          color: 'white',
          bg: 'navy',
          borderRadius: '5px',
          padding: '10px',
          textTransform: 'uppercase',
          fontSize: '0.875rem',
          _hover: {
            bg: 'orange',
            color: 'white',
            textDecoration: 'none',
          },
        },
        buttonInverse: {
          fontWeight: 'bold',
          bg: 'white',
          color: 'navy',
          transition: 'all 0.25s',
          padding: '1.5625rem 1.25rem',
          borderRadius: '3.125rem',
          display: 'inline-block',
          _hover: {
            bg: 'navy',
            color: 'white',
            textDecoration: 'none',
          },
        },
        primary: {
          bg: 'none',
          color: 'navy',
          padding: '0',
          minWidth: 'auto',
          paddingInline: '0',
          _hover: {
            bg: 'none',
            border: 'none',
            textDecoration: 'underline',
          },
        },
        footer: {
          bg: 'none',
          color: 'black',
          padding: '0',
          minWidth: 'auto',
          paddingInline: '0',
          _hover: {
            bg: 'none',
            border: 'none',
            color: 'navy',
            textDecoration: 'underline',
          },
        },
        header: {
          bg: 'none',
          color: 'black',
          padding: '10px',
          minWidth: 'auto',
          paddingInline: '10px',
          borderRadius: '5px',
          fontSize: 'lg',
          _hover: {
            bg: 'lightBlue',
            border: 'none',
            textDecoration: 'none',
            color: 'navy',
          },
        },
      },
    },
    Container: {
      baseStyle: {
        px: ['30px', '30px', '60px'],
      },
    },
  },
};

const theme = extendTheme({
  ...config,
  fonts: {
    body: "'AvenirNextWorld', sans-serif",
    heading: "'AvenirNextWorld', sans-serif",
  },
  ...colors,
  ...components,
  ...sizes,
  ...fontSizes,
  ...spacing,
  ...textStyles,
});

export const baseTheme = {};

export default theme;
